import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StickyNote, Plus, X, Pin, PinOff, Palette, GripVertical } from 'lucide-react';
import { getStickyNotes, createStickyNote, updateStickyNote, deleteStickyNote } from '../../api/stickyNote.api';
import { parseUTC } from '../../utils/dateUtils';

const NOTE_COLORS = [
  { hex: '#FEF3C7', name: 'Yellow', dark: 'rgba(245,158,11,0.15)' },
  { hex: '#DBEAFE', name: 'Blue', dark: 'rgba(59,130,246,0.15)' },
  { hex: '#D1FAE5', name: 'Green', dark: 'rgba(16,185,129,0.15)' },
  { hex: '#FCE7F3', name: 'Pink', dark: 'rgba(236,72,153,0.15)' },
  { hex: '#EDE9FE', name: 'Purple', dark: 'rgba(139,92,246,0.15)' },
  { hex: '#FEE2E2', name: 'Red', dark: 'rgba(239,68,68,0.15)' },
];

const DEFAULT_W = 240;
const DEFAULT_H = 200;

/**
 * Global Sticky Notes panel — Windows-style draggable notes.
 * Opened from navbar icon. Notes auto-save on blur.
 */
const GlobalStickyNotes = ({ isOpen, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const saveTimers = useRef({}); // debounce per note
  const isDark = document.documentElement.classList.contains('dark');

  // Load notes on mount (and refresh when panel opens)
  const loadNotes = useCallback(async () => {
    setLoading(true);
    const res = await getStickyNotes();
    if (res?.isSuccess && res.data) setNotes(res.data);
    else if (res?.data) setNotes(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Refresh when panel is re-opened
  useEffect(() => {
    if (isOpen) loadNotes();
  }, [isOpen, loadNotes]);

  // Auto-save a note (debounced 600ms)
  const scheduleAutoSave = useCallback((note) => {
    if (saveTimers.current[note.id]) clearTimeout(saveTimers.current[note.id]);
    saveTimers.current[note.id] = setTimeout(async () => {
      await updateStickyNote(note.id, {
        content: note.content,
        color: note.color,
        positionX: note.positionX,
        positionY: note.positionY,
        width: note.width,
        height: note.height,
        isPinned: note.isPinned,
        sortOrder: note.sortOrder,
      });
    }, 600);
  }, []);

  const handleCreate = async () => {
    const res = await createStickyNote({
      content: '',
      color: NOTE_COLORS[notes.length % NOTE_COLORS.length].hex,
      positionX: 60 + (notes.length % 4) * 30,
      positionY: 60 + (notes.length % 4) * 30,
    });
    if (res?.isSuccess && res.data) {
      setNotes(prev => [...prev, res.data]);
    }
  };

  const handleDelete = async (id) => {
    await deleteStickyNote(id);
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const updateLocal = (id, patch) => {
    setNotes(prev => prev.map(n => {
      if (n.id !== id) return n;
      const updated = { ...n, ...patch };
      scheduleAutoSave(updated);
      return updated;
    }));
  };

  const pinnedNotes = notes.filter(n => n.isPinned);

  return (
    <>
      {/* ── Pinned floating notes — ALWAYS visible regardless of panel state ── */}
      {pinnedNotes.length > 0 && (
        <div className="fixed inset-0 z-[60] pointer-events-none">
          {pinnedNotes.map(note => (
            <DraggableNote
              key={note.id}
              note={note}
              isDark={isDark}
              onUpdate={updateLocal}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* ── Dropdown panel — only when open ── */}
      {isOpen && (
        <div className="fixed inset-0 z-[60] pointer-events-none">
          {/* Backdrop — click to close */}
          <div className="absolute inset-0 pointer-events-auto" onClick={onClose} />

          {/* Floating panel header */}
          <div className="absolute top-16 right-4 pointer-events-auto z-[61]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-72 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">My Notes</span>
                  <span className="text-xs text-gray-400">({notes.length})</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={handleCreate}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="New note">
                    <Plus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                  <button onClick={onClose}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-6 text-center text-gray-400 text-sm">Loading...</div>
              ) : notes.length === 0 ? (
                <div className="p-6 text-center">
                  <StickyNote className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No notes yet</p>
                  <button onClick={handleCreate}
                          className="mt-3 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg text-xs font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors">
                    + Create a note
                  </button>
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto p-2 space-y-2">
                  {notes.map(note => (
                    <MiniNoteCard
                      key={note.id}
                      note={note}
                      isDark={isDark}
                      onUpdate={updateLocal}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>);

};

/* ─── Mini Note Card (in dropdown) ─── */
const MiniNoteCard = ({ note, isDark, onUpdate, onDelete }) => {
  const [showColors, setShowColors] = useState(false);
  const bgColor = isDark
    ? (NOTE_COLORS.find(c => c.hex === note.color)?.dark || 'rgba(245,158,11,0.15)')
    : (note.color || '#FEF3C7');

  return (
    <div className="rounded-xl border border-black/5 dark:border-white/5 overflow-hidden"
         style={{ backgroundColor: bgColor }}>
      <div className="flex items-start gap-1 px-2 pt-2 pb-0">
        <textarea
          className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 resize-none outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 min-h-[48px]"
          value={note.content}
          placeholder="Write something..."
          onChange={e => onUpdate(note.id, { content: e.target.value })}
          onBlur={() => onUpdate(note.id, {})} // triggers save
          rows={2}
        />
      </div>
      <div className="flex items-center justify-between px-2 py-1.5">
        <span className="text-[9px] text-gray-500 dark:text-gray-400 font-mono">
          {note.updatedAt ? parseUTC(note.updatedAt).toLocaleDateString() : ''}
        </span>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setShowColors(!showColors)}
                  className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
            <Palette className="w-3 h-3 text-gray-400" />
          </button>
          <button onClick={() => onUpdate(note.id, { isPinned: !note.isPinned })}
                  className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  title={note.isPinned ? 'Unpin from screen' : 'Pin to screen'}>
            {note.isPinned
              ? <PinOff className="w-3 h-3 text-amber-500" />
              : <Pin className="w-3 h-3 text-gray-400" />}
          </button>
          <button onClick={() => onDelete(note.id)}
                  className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors">
            <X className="w-3 h-3 text-red-400" />
          </button>
        </div>
      </div>
      {showColors && (
        <div className="flex items-center gap-1 px-2 pb-2">
          {NOTE_COLORS.map(c => (
            <button key={c.hex}
                    onClick={() => { onUpdate(note.id, { color: c.hex }); setShowColors(false); }}
                    className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: isDark ? c.dark : c.hex,
                      borderColor: note.color === c.hex ? '#6366F1' : 'transparent',
                    }}
                    title={c.name} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Draggable Floating Note (pinned to screen) ─── */
const DraggableNote = ({ note, isDark, onUpdate, onDelete }) => {
  const noteRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({
    x: note.positionX ?? 100,
    y: note.positionY ?? 100,
  });

  const bgColor = isDark
    ? (NOTE_COLORS.find(c => c.hex === note.color)?.dark || 'rgba(245,158,11,0.15)')
    : (note.color || '#FEF3C7');

  const onMouseDown = (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
    setDragging(true);
    dragOffset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      setPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => {
      setDragging(false);
      onUpdate(note.id, {
        positionX: pos.x,
        positionY: pos.y,
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, pos, note.id, onUpdate]);

  return (
    <div
      ref={noteRef}
      className="absolute pointer-events-auto shadow-xl rounded-xl border border-black/10 dark:border-white/10 overflow-hidden"
      style={{
        left: pos.x,
        top: pos.y,
        width: note.width || DEFAULT_W,
        minHeight: note.height || DEFAULT_H,
        backgroundColor: bgColor,
        zIndex: 62,
        cursor: dragging ? 'grabbing' : 'default',
      }}
      onMouseDown={onMouseDown}
    >
      {/* Drag handle */}
      <div className="flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing select-none"
           style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-1.5">
          <GripVertical className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Note</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onUpdate(note.id, { isPinned: false })}
                  className="p-1 rounded hover:bg-black/10 transition-colors" title="Unpin">
            <PinOff className="w-3 h-3 text-amber-600" />
          </button>
          <button onClick={() => onDelete(note.id)}
                  className="p-1 rounded hover:bg-red-200/50 transition-colors">
            <X className="w-3 h-3 text-red-500" />
          </button>
        </div>
      </div>
      <textarea
        className="w-full bg-transparent text-sm text-gray-800 dark:text-gray-200 resize-none outline-none p-3 placeholder:text-gray-400 dark:placeholder:text-gray-500"
        value={note.content}
        placeholder="Write something..."
        onChange={e => onUpdate(note.id, { content: e.target.value })}
        onBlur={() => onUpdate(note.id, {})}
        style={{ minHeight: (note.height || DEFAULT_H) - 44 }}
      />
      <div className="px-3 pb-2">
        <span className="text-[9px] text-gray-500 dark:text-gray-400 font-mono">
          {note.updatedAt ? parseUTC(note.updatedAt).toLocaleString() : ''}
        </span>
      </div>
    </div>
  );
};

export default GlobalStickyNotes;
